import { CategoryData, KamaGuideWord } from '../../../../types/types';

// Parcours méthodologique, suite de « lecture.ts » : une fois l'alphabet et
// les notions de signe-directeur/déterminatif acquises, comment ces mots
// s'enchaînent en phrase. Chaque règle tient sur UNE fiche guidée par la
// mascotte (`guide`, rendue par KamaGuideCard) : l'ibis explique la règle
// en quelques bulles courtes pendant que la phrase, découpée mot à mot,
// allume les mots dont il parle — la dernière bulle bascule sur un second
// exemple pour fixer la règle. Remplace l'ancien trio de fiches
// phrase / analyse (long paragraphe) / second exemple, trop lourd à lire.
// Un QCM récapitulatif ferme chacune des trois leçons. Faits d'ordre des mots
// standard, présents dans toute grammaire de l'égyptien classique (Gardiner,
// Egyptian Grammar ; Allen, Middle Egyptian). Chaque mot n'utilise que des
// signes vérifiés un par un via la base Unicode (voir la skill
// kama-hieroglyph-decoder) — jamais devinés : les exemples ne recombinent
// que des mots déjà attestés dans ce fichier (sḏm, zẖꜣw, mdt, nsw, nṯr, pr,
// pw, ꜥꜣ, nfr, f, s, ink) ou dans corps.ts (rd), jamais un signe inédit.

// Mots réutilisés d'une règle à l'autre.
const SDM: KamaGuideWord = { glyphs: '𓋴𓆓𓅓𓄔', translit: 'sḏm', gloss: 'entend', glossEn: 'hears' };
const ZSHAW: KamaGuideWord = { glyphs: '𓏞𓅱𓀀', translit: 'zẖꜣw', gloss: 'le scribe', glossEn: 'the scribe' };
const MDT: KamaGuideWord = { glyphs: '𓌃𓏏', translit: 'mdt', gloss: 'les mots', glossEn: 'the words' };
const NSW: KamaGuideWord = { glyphs: '𓇓', translit: 'nsw', gloss: 'le roi', glossEn: 'the king' };
const NTR: KamaGuideWord = { glyphs: '𓊹', translit: 'nṯr', gloss: 'le dieu', glossEn: 'the god' };
const PR: KamaGuideWord = { glyphs: '𓉐', translit: 'pr', gloss: 'la maison', glossEn: 'the house' };
const M: KamaGuideWord = { glyphs: '𓅓', translit: 'm', gloss: 'dans', glossEn: 'in' };
const N_OF: KamaGuideWord = { glyphs: '𓈖', translit: 'n', gloss: 'de', glossEn: 'of' };

// Le parcours est découpé en trois leçons courtes — trois nœuds du sentier
// (« Phrase », « Phrase — le nom », « Phrase — pronoms », voir
// CategorySelection.tsx's UNIT_CONFIGS) — plutôt qu'une seule de quatorze
// fiches. « Phrase » garde sa clé : une complétion déjà enregistrée reste
// valable pour la première leçon.

// L'ordre des mots : les trois types de phrases, avec les prépositions.
export const phrase: CategoryData = {
    'Construire une phrase': {
        translation: 'Trois types de phrases, un ordre des mots qui change tout',
        translationEn: 'Three types of sentences, a word order that changes everything',
        illustration: '𓏞𓅱𓀀',
        guide: {
            sentences: [],
            steps: [
                {
                    text: 'Tu sais lire les signes : passons aux phrases ! Ensemble, on va construire tes premières phrases égyptiennes.',
                    textEn: 'You can read the signs: on to sentences! Together, we\'ll build your first Egyptian sentences.',
                },
                {
                    text: 'Beaucoup de langues changent la fin des mots pour dire qui fait quoi. À l\'écrit, l\'égyptien le fait très peu.',
                    textEn: 'Many languages change word endings to show who does what. In writing, Egyptian barely does.',
                },
                {
                    text: 'Son secret, c\'est l\'ordre des mots. Je vais t\'apprendre trois modèles de phrases, puis quelques petits outils : pronoms, pluriel, « de ».',
                    textEn: 'Its secret is word order. I\'ll teach you three sentence patterns, then a few small tools: pronouns, plural, "of".',
                },
                {
                    text: 'Sur chaque carte, regarde les mots qui s\'allument : ce sont ceux dont je parle. Appuie sur Suivant pour m\'écouter jusqu\'au bout.',
                    textEn: 'On each card, watch the words that light up: they\'re the ones I\'m talking about. Tap Next to hear me out.',
                },
            ],
        },
    },
    'Ordre verbe-sujet-objet': {
        translation: 'Le verbe se place en premier — sḏm zẖꜣw mdt, « le scribe entend les mots »',
        translationEn: 'The verb comes first — sḏm zẖꜣw mdt, "the scribe hears the words"',
        illustration: '𓋴𓆓𓅓𓄔𓏞𓅱𓀀𓌃𓏏',
        guide: {
            sentences: [
                { words: [SDM, ZSHAW, MDT], translation: 'le scribe entend les mots', translationEn: 'the scribe hears the words' },
                { words: [SDM, NSW, MDT], translation: 'le roi entend les mots', translationEn: 'the king hears the words' },
            ],
            steps: [
                {
                    text: 'Voici une phrase complète : « le scribe entend les mots ». Trois mots, comme en français… mais pas dans le même ordre !',
                    textEn: 'Here\'s a full sentence: "the scribe hears the words". Three words, just like English… but not in the same order!',
                },
                {
                    text: 'Le premier mot, sḏm, c\'est le verbe « entend ». Tu vois l\'oreille au bout ? Elle annonce un mot lié à l\'ouïe.',
                    textEn: 'The first word, sḏm, is the verb "hears". See the ear at the end? It signals a word about hearing.',
                    highlight: [0],
                },
                {
                    text: 'Vient ensuite le sujet, zẖꜣw, « le scribe » : sa palette, puis le petit homme assis.',
                    textEn: 'Next comes the subject, zẖꜣw, "the scribe": his palette, then the little seated man.',
                    highlight: [1],
                },
                {
                    text: 'Et enfin l\'objet, mdt, « les mots ». Verbe, sujet, objet : mot à mot, « entend le scribe les mots ».',
                    textEn: 'And finally the object, mdt, "the words". Verb, subject, object: word for word, "hears the scribe the words".',
                    highlight: [2],
                },
                {
                    text: 'Change le sujet : sḏm nsw mdt, « le roi entend les mots ». Le verbe reste en tête. Retiens-le : en égyptien, le verbe ouvre la phrase !',
                    textEn: 'Change the subject: sḏm nsw mdt, "the king hears the words". The verb stays in front. Remember: in Egyptian, the verb opens the sentence!',
                    sentence: 1,
                    highlight: [0],
                },
            ],
        },
    },
    'Les prépositions': {
        translation: 'Comme en français : la préposition précède son complément — m pr, « dans la maison »',
        translationEn: 'As in English: the preposition precedes its complement — m pr, "in the house"',
        illustration: '𓅓𓉐',
        guide: {
            sentences: [
                { words: [M, PR], translation: 'dans la maison', translationEn: 'in the house' },
                {
                    words: [{ glyphs: '𓈖', translit: 'n', gloss: 'pour', glossEn: 'for' }, NTR],
                    translation: 'pour le dieu',
                    translationEn: 'for the god',
                },
            ],
            steps: [
                {
                    text: 'Bonne nouvelle : ici, c\'est comme en français ! Le petit mot m — la chouette que tu connais — veut dire « dans ».',
                    textEn: 'Good news: here it works just like English! The little word m — the owl you already know — means "in".',
                    highlight: [0],
                },
                {
                    text: 'Il se place avant son complément, pr, « la maison » : m pr, « dans la maison ». Exactement notre ordre.',
                    textEn: 'It goes before its complement, pr, "the house": m pr, "in the house". Exactly our order.',
                    highlight: [1],
                },
                {
                    text: 'Autre préposition : n, l\'eau qui ondule, « pour ». n nṯr, « pour le dieu ». Même règle : la préposition d\'abord.',
                    textEn: 'Another preposition: n, the rippling water, "for". n nṯr, "for the god". Same rule: preposition first.',
                    sentence: 1,
                    highlight: [0],
                },
                {
                    text: 'Tu en croiseras d\'autres, comme r, « vers », ou ḥr, « sur ». Elles aussi passent toujours devant.',
                    textEn: 'You\'ll come across others, like r, "toward", or ḥr, "on". They always go in front too.',
                    sentence: 1,
                },
            ],
        },
    },
    'La phrase adverbiale': {
        translation: 'Sujet + lieu, sans verbe « être » — nsw m pr-ꜥꜣ, « le roi est dans le palais »',
        translationEn: 'Subject + place, with no verb "to be" — nsw m pr-ꜥꜣ, "the king is in the palace"',
        illustration: '𓇓𓅓𓉐𓉻',
        guide: {
            sentences: [
                {
                    words: [NSW, M, { glyphs: '𓉐𓉻', translit: 'pr-ꜥꜣ', gloss: 'le palais', glossEn: 'the palace' }],
                    translation: 'le roi est dans le palais',
                    translationEn: 'the king is in the palace',
                },
                { words: [NTR, M, PR], translation: 'le dieu est dans la maison', translationEn: 'the god is in the house' },
            ],
            steps: [
                {
                    text: '« Le roi est dans le palais. » Cherche le verbe « être »… Il n\'y en a pas !',
                    textEn: '"The king is in the palace." Look for the verb "to be"… There isn\'t one!',
                },
                {
                    text: 'On pose d\'abord le sujet : nsw, « le roi » — la plante du Sud, symbole royal.',
                    textEn: 'First comes the subject: nsw, "the king" — the sedge plant of the South, a royal symbol.',
                    highlight: [0],
                },
                {
                    text: 'Puis le lieu : m pr-ꜥꜣ, « dans le palais ». Sujet + lieu, et c\'est tout : le « est » se devine.',
                    textEn: 'Then the place: m pr-ꜥꜣ, "in the palace". Subject + place, that\'s all: the "is" is understood.',
                    highlight: [1, 2],
                },
                {
                    text: 'pr-ꜥꜣ, c\'est « la grande maison » : pr, la maison, et ꜥꜣ, grand. Ce nom a fini par désigner le roi lui-même… c\'est l\'origine du mot « pharaon » !',
                    textEn: 'pr-ꜥꜣ means "the great house": pr, house, and ꜥꜣ, great. The name ended up meaning the king himself… it\'s where the word "pharaoh" comes from!',
                    highlight: [2],
                },
                {
                    text: 'À toi de lire : nṯr m pr, « le dieu est dans la maison ». Sujet, puis lieu.',
                    textEn: 'Your turn to read: nṯr m pr, "the god is in the house". Subject, then place.',
                    sentence: 1,
                    highlight: [0, 1, 2],
                },
            ],
        },
    },
    'La phrase nominale': {
        translation: 'A = B : le mot pw joue le rôle du verbe « être » — zẖꜣw pw, « c\'est un scribe »',
        translationEn: 'A = B: the word pw plays the role of the verb "to be" — zẖꜣw pw, "he is a scribe"',
        illustration: '𓏞𓅱𓀀𓊪𓅱',
        guide: {
            sentences: [
                {
                    words: [
                        { glyphs: '𓏞𓅱𓀀', translit: 'zẖꜣw', gloss: 'scribe', glossEn: 'scribe' },
                        { glyphs: '𓊪𓅱', translit: 'pw', gloss: 'ceci', glossEn: 'this' },
                    ],
                    translation: 'c\'est un scribe',
                    translationEn: 'he is a scribe',
                },
                {
                    words: [
                        { glyphs: '𓊹', translit: 'nṯr', gloss: 'dieu', glossEn: 'god' },
                        { glyphs: '𓊪𓅱', translit: 'pw', gloss: 'ceci', glossEn: 'this' },
                    ],
                    translation: 'c\'est un dieu',
                    translationEn: 'it is a god',
                },
            ],
            steps: [
                {
                    text: 'Pour dire « A est B », pas de verbe « être » non plus. Mais il faut un petit mot pour relier les deux.',
                    textEn: 'To say "A is B", there\'s no verb "to be" either. But you need a little word to link the two.',
                },
                {
                    text: 'Ce mot, c\'est pw, « ceci » : le tabouret p et le poussin w. Il se place après le nom.',
                    textEn: 'That word is pw, "this": the stool p and the chick w. It goes after the noun.',
                    highlight: [1],
                },
                {
                    text: 'zẖꜣw pw, mot à mot « scribe ceci », veut dire « c\'est un scribe ». C\'est la phrase nominale.',
                    textEn: 'zẖꜣw pw, word for word "scribe this", means "he is a scribe". That\'s the nominal sentence.',
                    highlight: [0, 1],
                },
                {
                    text: 'Même modèle : nṯr pw, « c\'est un dieu ». Le nom d\'abord, pw ensuite.',
                    textEn: 'Same pattern: nṯr pw, "it is a god". The noun first, then pw.',
                    sentence: 1,
                    highlight: [1],
                },
            ],
        },
    },
    'Récapitulatif': {
        translation: 'Un dernier test pour vérifier que l\'ordre des mots est bien acquis',
        translationEn: 'A final quiz to check that word order is well understood',
        illustration: '𓋴𓆓𓅓𓄔𓏞𓅱𓀀𓌃𓏏',
        quiz: {
            question: 'Range ces trois mots égyptiens dans le bon ordre pour former une phrase verbale correcte : sḏm (« entend »), zẖꜣw (« le scribe »), mdt (« les mots »).',
            questionEn: 'Arrange these three Egyptian words in the correct order to form a proper verbal sentence: sḏm ("hears"), zẖꜣw ("the scribe"), mdt ("the words").',
            options: [
                'sḏm zẖꜣw mdt',
                'zẖꜣw sḏm mdt',
                'mdt sḏm zẖꜣw',
                'zẖꜣw mdt sḏm',
            ],
            correctIndex: 0,
            explanation: 'sḏm zẖꜣw mdt (𓋴𓆓𓅓𓄔𓏞𓅱𓀀𓌃𓏏) respecte l\'ordre verbe-sujet-objet (VSO) de l\'égyptien classique : le verbe sḏm (« entend ») ouvre la phrase, suivi de son sujet zẖꜣw (« le scribe »), puis de l\'objet mdt (« les mots ») — littéralement « entend le-scribe les-mots », traduit « le scribe entend les mots ». Retiens ce réflexe pour toute phrase verbale ; les prépositions (m, n, r, ḥr) précèdent leur complément comme en français ; une phrase adverbiale (sujet + lieu) ou nominale (A pw B) n\'a besoin d\'aucun verbe « être ».',
            explanationEn: 'sḏm zẖꜣw mdt (𓋴𓆓𓅓𓄔𓏞𓅱𓀀𓌃𓏏) follows the verb-subject-object (VSO) order of Classical Egyptian: the verb sḏm ("hears") opens the sentence, followed by its subject zẖꜣw ("the scribe"), then the object mdt ("the words") — literally "hears the-scribe the-words," translated as "the scribe hears the words." Keep this reflex for every verbal sentence; prepositions (m, n, r, ḥr) precede their complement as in English; an adverbial sentence (subject + place) or nominal sentence (A pw B) needs no verb "to be".',
        },
    },
};

// Ce qui accompagne un nom : l'adjectif et les deux façons de dire « de ».
export const phraseNom: CategoryData = {
    'L\'adjectif épithète': {
        translation: 'L\'adjectif suit toujours le nom — nṯr nfr, « le bon dieu »',
        translationEn: 'The adjective always follows the noun — nṯr nfr, "the good god"',
        illustration: '𓊹𓄤',
        guide: {
            sentences: [
                {
                    words: [
                        { glyphs: '𓊹', translit: 'nṯr', gloss: 'dieu', glossEn: 'god' },
                        { glyphs: '𓄤', translit: 'nfr', gloss: 'bon', glossEn: 'good' },
                    ],
                    translation: 'le bon dieu',
                    translationEn: 'the good god',
                },
                {
                    words: [
                        { glyphs: '𓊹', translit: 'nṯr', gloss: 'dieu', glossEn: 'god' },
                        { glyphs: '𓉻', translit: 'ꜥꜣ', gloss: 'grand', glossEn: 'great' },
                    ],
                    translation: 'le grand dieu',
                    translationEn: 'the great god',
                },
            ],
            steps: [
                {
                    text: 'Voici nṯr, « dieu » : un mât surmonté d\'un fanion, l\'emblème des sanctuaires.',
                    textEn: 'Here\'s nṯr, "god": a pole topped with a pennant, the emblem of shrines.',
                    highlight: [0],
                },
                {
                    text: 'Pour dire « bon », on ajoute nfr. Attention : en égyptien, l\'adjectif vient toujours après le nom.',
                    textEn: 'To say "good", you add nfr. Careful: in Egyptian, the adjective always comes after the noun.',
                    highlight: [1],
                },
                {
                    text: 'nṯr nfr, mot à mot « dieu bon » : « le bon dieu ». Ce titre royal est gravé partout sur les monuments.',
                    textEn: 'nṯr nfr, word for word "god good": "the good god". This royal title is carved all over the monuments.',
                    highlight: [0, 1],
                },
                {
                    text: 'Même place pour ꜥꜣ, « grand » : nṯr ꜥꜣ, « le grand dieu ». Le nom d\'abord, l\'adjectif ensuite.',
                    textEn: 'Same spot for ꜥꜣ, "great": nṯr ꜥꜣ, "the great god". The noun first, the adjective after.',
                    sentence: 1,
                    highlight: [1],
                },
            ],
        },
    },
    'Le génitif direct': {
        translation: 'Juxtaposer deux noms suffit — pr nsw, « la maison du roi »',
        translationEn: 'Simply juxtaposing two nouns is enough — pr nsw, "the king\'s house"',
        illustration: '𓉐𓇓',
        guide: {
            sentences: [
                {
                    words: [PR, { glyphs: '𓇓', translit: 'nsw', gloss: 'roi', glossEn: 'king' }],
                    translation: 'la maison du roi',
                    translationEn: 'the king\'s house',
                },
                {
                    words: [PR, { glyphs: '𓊹', translit: 'nṯr', gloss: 'dieu', glossEn: 'god' }],
                    translation: 'le temple (la maison du dieu)',
                    translationEn: 'the temple (the god\'s house)',
                },
            ],
            steps: [
                {
                    text: 'Pour dire « la maison du roi », il suffit de mettre les deux noms côte à côte : pr nsw.',
                    textEn: 'To say "the king\'s house", just put the two nouns side by side: pr nsw.',
                    highlight: [0, 1],
                },
                {
                    text: 'L\'ordre compte : d\'abord ce qu\'on possède, pr, « la maison »…',
                    textEn: 'Order matters: first the thing owned, pr, "the house"…',
                    highlight: [0],
                },
                {
                    text: '…puis le possesseur, nsw, « le roi ». Mot à mot « maison roi ». C\'est le génitif direct.',
                    textEn: '…then the owner, nsw, "the king". Word for word "house king". That\'s the direct genitive.',
                    highlight: [1],
                },
                {
                    text: 'Même chose avec pr nṯr, « la maison du dieu »… autrement dit, le temple !',
                    textEn: 'Same with pr nṯr, "the god\'s house"… in other words, the temple!',
                    sentence: 1,
                    highlight: [0, 1],
                },
            ],
        },
    },
    'Le génitif indirect': {
        translation: 'Avec n, « de » — zẖꜣw n nsw, « le scribe du roi »',
        translationEn: 'With n, "of" — zẖꜣw n nsw, "the king\'s scribe"',
        illustration: '𓏞𓅱𓀀𓈖𓇓',
        guide: {
            sentences: [
                { words: [ZSHAW, N_OF, NSW], translation: 'le scribe du roi', translationEn: 'the king\'s scribe' },
                { words: [MDT, N_OF, NSW], translation: 'les mots du roi', translationEn: 'the king\'s words' },
            ],
            steps: [
                {
                    text: 'Autre façon de dire « le scribe du roi » : on garde les deux noms…',
                    textEn: 'Another way to say "the king\'s scribe": keep the two nouns…',
                    highlight: [0, 2],
                },
                {
                    text: '…et on glisse entre eux n, l\'eau qui ondule. Ici, il veut dire « de ».',
                    textEn: '…and slip n, the rippling water, between them. Here it means "of".',
                    highlight: [1],
                },
                {
                    text: 'zẖꜣw n nsw, mot à mot « scribe de roi ». C\'est le génitif indirect : plus clair que deux noms collés.',
                    textEn: 'zẖꜣw n nsw, word for word "scribe of king". That\'s the indirect genitive: clearer than two nouns side by side.',
                    highlight: [0, 1, 2],
                },
                {
                    text: 'À toi : mdt n nsw, « les mots du roi ». Cette tournure devient de plus en plus courante au fil des siècles.',
                    textEn: 'Your turn: mdt n nsw, "the king\'s words". This construction becomes more and more common over the centuries.',
                    sentence: 1,
                    highlight: [1],
                },
            ],
        },
    },
    'Récapitulatif — le nom': {
        translation: 'Un test pour vérifier l\'adjectif et les deux génitifs',
        translationEn: 'A quiz to check the adjective and the two genitives',
        illustration: '𓉐𓇓',
        quiz: {
            question: 'Comment écrire « la maison du roi » avec le génitif direct ?',
            questionEn: 'How do you write "the king\'s house" with the direct genitive?',
            options: [
                'pr nsw',
                'nsw pr',
                'pr n nsw',
                'm pr nsw',
            ],
            correctIndex: 0,
            explanation: 'pr nsw (𓉐𓇓) : avec le génitif direct, on pose simplement les deux noms côte à côte, ce qu\'on possède d\'abord (pr, « la maison »), le possesseur ensuite (nsw, « le roi »). pr n nsw serait aussi correct, mais c\'est le génitif indirect, avec n, « de ». nsw pr inverse l\'ordre, et m pr nsw voudrait dire « dans la maison du roi ». Même logique que l\'adjectif : ce qui précise le nom vient après lui (nṯr nfr, « le bon dieu »).',
            explanationEn: 'pr nsw (𓉐𓇓): with the direct genitive, the two nouns simply sit side by side, the thing owned first (pr, "the house"), the owner second (nsw, "the king"). pr n nsw would also be correct, but that is the indirect genitive, with n, "of". nsw pr reverses the order, and m pr nsw would mean "in the king\'s house". Same logic as the adjective: whatever specifies the noun comes after it (nṯr nfr, "the good god").',
        },
    },
};

// Pronoms et nombre : les possessifs collés, les pronoms autonomes, le pluriel et le duel.
export const phrasePronoms: CategoryData = {
    'Les pronoms suffixes': {
        translation: 'Le possessif se colle au nom, sans mot séparé — pr.f, « sa maison »',
        translationEn: 'The possessive attaches to the noun, with no separate word — pr.f, "his house"',
        illustration: '𓉐𓆑',
        guide: {
            sentences: [
                {
                    words: [PR, { glyphs: '𓆑', translit: '.f', gloss: 'à lui', glossEn: 'his' }],
                    translation: 'sa maison (à lui)',
                    translationEn: 'his house',
                },
                {
                    words: [PR, { glyphs: '𓋴', translit: '.s', gloss: 'à elle', glossEn: 'her' }],
                    translation: 'sa maison (à elle)',
                    translationEn: 'her house',
                },
            ],
            steps: [
                {
                    text: 'Voici pr, « la maison ». Comment dire « sa maison » ? Pas besoin d\'un mot en plus…',
                    textEn: 'Here\'s pr, "the house". How do you say "his house"? No extra word needed…',
                    highlight: [0],
                },
                {
                    text: '…on colle un seul signe à la fin : la vipère f, « à lui ». pr.f, mot à mot « maison-lui » : « sa maison ».',
                    textEn: '…you stick a single sign on the end: the viper f, "his". pr.f, word for word "house-him": "his house".',
                    highlight: [1],
                },
                {
                    text: 'Pour « à elle », on colle .s, le tissu plié : pr.s, « sa maison (à elle) ».',
                    textEn: 'For "her", you stick on .s, the folded cloth: pr.s, "her house".',
                    sentence: 1,
                    highlight: [1],
                },
                {
                    text: 'Il en existe d\'autres : .i « mon », .k « ton », .n « notre ». Toujours un seul signe, collé au mot.',
                    textEn: 'There are others: .i "my", .k "your", .n "our". Always a single sign, stuck to the word.',
                    sentence: 1,
                },
            ],
        },
    },
    'Les pronoms indépendants': {
        translation: 'ink, « moi » : un mot autonome, pour insister ou ouvrir une phrase nominale',
        translationEn: 'ink, "I": a standalone word, used for emphasis or to open a nominal sentence',
        illustration: '𓇋𓈖𓂓',
        guide: {
            sentences: [
                {
                    words: [{ glyphs: '𓇋𓈖𓂓', translit: 'ink', gloss: 'moi', glossEn: 'I, me' }],
                    translation: 'moi, je',
                    translationEn: 'I, me',
                },
                {
                    words: [
                        { glyphs: '𓇋𓈖𓂓', translit: 'ink', gloss: 'moi', glossEn: 'I' },
                        { glyphs: '𓏞𓅱𓀀', translit: 'zẖꜣw', gloss: 'scribe', glossEn: 'scribe' },
                    ],
                    translation: 'moi, je suis scribe',
                    translationEn: 'I am a scribe',
                },
            ],
            steps: [
                {
                    text: 'Les pronoms ne sont pas tous collés. ink, « moi », est un vrai mot, qui tient tout seul.',
                    textEn: 'Not all pronouns are stuck on. ink, "I, me", is a real word that stands on its own.',
                    highlight: [0],
                },
                {
                    text: 'On s\'en sert pour insister, comme « c\'est moi qui… ».',
                    textEn: 'It\'s used for emphasis, like "it is I who…".',
                    highlight: [0],
                },
                {
                    text: 'Ou pour ouvrir une phrase nominale : ink zẖꜣw, « moi, je suis scribe ». Toujours sans verbe « être » !',
                    textEn: 'Or to open a nominal sentence: ink zẖꜣw, "I am a scribe". Still no verb "to be"!',
                    sentence: 1,
                    highlight: [0, 1],
                },
            ],
        },
    },
    'Le pluriel': {
        translation: 'Trois traits marquent le pluriel — nṯrw, « les dieux »',
        translationEn: 'Three strokes mark the plural — nṯrw, "the gods"',
        illustration: '𓊹𓏥',
        guide: {
            sentences: [
                {
                    words: [
                        { glyphs: '𓊹', translit: 'nṯr', gloss: 'dieu', glossEn: 'god' },
                        { glyphs: '𓏥', translit: '-w', gloss: 'pluriel', glossEn: 'plural' },
                    ],
                    translation: 'les dieux',
                    translationEn: 'the gods',
                },
                {
                    words: [
                        { glyphs: '𓉐', translit: 'pr', gloss: 'maison', glossEn: 'house' },
                        { glyphs: '𓏥', translit: '-w', gloss: 'pluriel', glossEn: 'plural' },
                    ],
                    translation: 'les maisons',
                    translationEn: 'the houses',
                },
            ],
            steps: [
                {
                    text: 'nṯr, c\'est « dieu ». Comment en faire « les dieux » ?',
                    textEn: 'nṯr means "god". How do you turn it into "the gods"?',
                    highlight: [0],
                },
                {
                    text: 'On ajoute trois petits traits après le mot. Ils marquent le pluriel, qui se lit -w : nṯrw.',
                    textEn: 'You add three little strokes after the word. They mark the plural, read -w: nṯrw.',
                    highlight: [1],
                },
                {
                    text: 'Parfois, au lieu des traits, le scribe répète le signe trois fois. Même sens : plusieurs !',
                    textEn: 'Sometimes, instead of the strokes, the scribe repeats the sign three times. Same meaning: several!',
                    highlight: [0, 1],
                },
                {
                    text: 'À toi : pr, « maison », devient prw, « les maisons ».',
                    textEn: 'Your turn: pr, "house", becomes prw, "the houses".',
                    sentence: 1,
                    highlight: [0, 1],
                },
            ],
        },
    },
    'Le duel': {
        translation: 'Deux traits marquent le duel — rdwy, « les deux jambes »',
        translationEn: 'Two strokes mark the dual — rdwy, "the two legs"',
        illustration: '𓂾𓏭',
        guide: {
            sentences: [
                {
                    words: [
                        { glyphs: '𓂾', translit: 'rd', gloss: 'jambe', glossEn: 'leg' },
                        { glyphs: '𓏭', translit: '-wy', gloss: 'les deux', glossEn: 'the two' },
                    ],
                    translation: 'les deux jambes',
                    translationEn: 'the two legs',
                },
            ],
            steps: [
                {
                    text: 'Voici rd, « la jambe ». Et si on parle des deux jambes ?',
                    textEn: 'Here\'s rd, "the leg". What if we mean both legs?',
                    highlight: [0],
                },
                {
                    text: 'L\'égyptien a une forme spéciale pour « deux » exactement : le duel. Il se marque par deux traits, qui se lisent -wy.',
                    textEn: 'Egyptian has a special form for exactly "two": the dual. It\'s marked with two strokes, read -wy.',
                    highlight: [1],
                },
                {
                    text: 'rdwy, « les deux jambes ». Pratique pour tout ce qui va par paire : les mains, les yeux…',
                    textEn: 'rdwy, "the two legs". Handy for anything that comes in pairs: hands, eyes…',
                    highlight: [0, 1],
                },
                {
                    text: 'Ces deux traits, tu les as déjà vus : c\'est aussi une façon d\'écrire la lettre y. Un signe, deux rôles !',
                    textEn: 'You\'ve seen these two strokes before: they\'re also a way of writing the letter y. One sign, two jobs!',
                    highlight: [1],
                },
            ],
        },
    },
    'Récapitulatif — pronoms et pluriel': {
        translation: 'Un second test pour vérifier pronoms suffixes et marque du pluriel',
        translationEn: 'A second quiz to check suffix pronouns and the plural marker',
        illustration: '𓉐𓆑',
        quiz: {
            question: 'Dans pr.f (𓉐𓆑), « sa maison », quel signe joue à lui seul le rôle du possessif « son, sa » ?',
            questionEn: 'In pr.f (𓉐𓆑), "his house," which sign alone plays the role of the possessive "his, her"?',
            options: [
                '𓉐 (pr, « maison »)',
                '𓆑 (f)',
                '𓈖 (n)',
                '𓏥 (les trois traits du pluriel)',
            ],
            optionsEn: [
                '𓉐 (pr, "house")',
                '𓆑 (f)',
                '𓈖 (n)',
                '𓏥 (the three strokes of the plural)',
            ],
            correctIndex: 1,
            explanation: 'Le signe f (I9), déjà rencontré comme la consonne f de l\'alphabet, joue ici le rôle du pronom suffixe possessif « son, sa » : pr.f, littéralement « maison-lui », se traduit « sa maison ». Retiens ce principe : les pronoms suffixes égyptiens réutilisent souvent des lettres déjà connues de l\'alphabet plutôt que d\'être des mots séparés — exactement comme k (« ton »), s (« sa », à elle) ou n (« notre »). Le pluriel, lui, s\'indique par trois traits (Z2) après le mot : nṯr, « dieu », devient nṯrw, « les dieux ».',
            explanationEn: 'The sign f (I9), already encountered as the consonant f of the alphabet, here plays the role of the possessive suffix pronoun "his, her": pr.f, literally "house-him," translates as "his house." Keep this principle in mind: Egyptian suffix pronouns often reuse letters already known from the alphabet rather than being separate words — exactly like k ("your"), s ("her") or n ("our"). The plural, meanwhile, is indicated by three strokes (Z2) after the word: nṯr, "god," becomes nṯrw, "the gods."',
        },
    },
};

export default phrase;
